import { useEffect, useRef } from 'react';
import type { ReactNode, MouseEvent } from 'react';
import { X } from 'lucide-react';
import './Modal.css';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    title?: ReactNode;
    children: ReactNode;
    footer?: ReactNode;
    /** Extra class on the dialog box, e.g. for width overrides. */
    className?: string;
    /** Prevent closing by clicking the backdrop or pressing Escape (e.g. during submit). */
    locked?: boolean;
    /** Larger dialog (max-width 800px). */
    size?: 'md' | 'lg' | 'xl';
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/**
 * Accessible modal: focus trap, Escape to close, restores focus, locks body scroll.
 * Reuses the global .modal-overlay / .modal styles from index.css.
 */
export function Modal({ open, onClose, title, children, footer, className = '', locked = false, size = 'md' }: ModalProps) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<Element | null>(null);

    useEffect(() => {
        if (!open) return;
        previouslyFocused.current = document.activeElement;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const node = dialogRef.current;
        const first = node?.querySelector<HTMLElement>(FOCUSABLE);
        (first ?? node)?.focus();

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !locked) {
                e.stopPropagation();
                onClose();
                return;
            }
            if (e.key === 'Tab' && node) {
                const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el => el.offsetParent !== null);
                if (focusables.length === 0) { e.preventDefault(); return; }
                const firstEl = focusables[0];
                const lastEl = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
                else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
            (previouslyFocused.current as HTMLElement | null)?.focus?.();
        };
    }, [open, onClose, locked]);

    if (!open) return null;

    const onBackdrop = (e: MouseEvent) => {
        if (e.target === e.currentTarget && !locked) onClose();
    };

    return (
        <div className="modal-overlay" onMouseDown={onBackdrop}>
            <div
                ref={dialogRef}
                className={`modal ui-modal ui-modal-${size} ${className}`}
                role="dialog"
                aria-modal="true"
                aria-label={typeof title === 'string' ? title : undefined}
                tabIndex={-1}
            >
                {title !== undefined && (
                    <div className="modal-header ui-modal-header">
                        <h2 className="ui-modal-title">{title}</h2>
                        <button type="button" className="ui-modal-close" onClick={onClose} aria-label="Close" disabled={locked}>
                            <X size={20} />
                        </button>
                    </div>
                )}
                <div className="modal-body ui-modal-body">{children}</div>
                {footer && <div className="modal-footer ui-modal-footer">{footer}</div>}
            </div>
        </div>
    );
}
